/**
 * My settings (JF 2026-10-16: colleagues could not find the GP coefficients or the exchange rate — they were spread over
 * the cost sheet and the admin page): my default quote coefficients (base + per quantity), the exchange rate new sheets
 * start with, and how amounts / language are shown. The AI status stays at the bottom. The API key lives on the server
 * only (JF 2026-09-30).
 */
import { useEffect, useState } from "react";
import { n } from "@ic/engine";
import { api } from "../../shared/api.ts";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { Modal } from "../../shared/ui/common.tsx";
import { CoefEditor } from "./CoefEditor.tsx";

export function Settings() {
  const t = useT();
  const meta = useStore((s) => s.meta);
  const user = useStore((s) => s.user);
  const cur = useStore((s) => s.cur);
  const lang = useStore((s) => s.lang);
  const dfx = useStore((s) => s.defaultFx);
  const { closeModal, setCur, setLang, toast } = useStore.getState();
  const [fx, setFx] = useState(String(dfx));
  useEffect(() => setFx(String(dfx)), [dfx]);
  const admin = user?.role === "admin";
  const ai = meta?.ai;
  async function saveFx() {
    const v = n(fx);
    if (!(v > 0)) return;
    try { await api.saveCompanyFx(v); useStore.getState().setDefaultFx(v); toast(t("Exchange rate saved — new cost sheets use it", "汇率已保存——新建的成本表都用这个")); }
    catch (e) { toast(e instanceof Error ? e.message : String(e)); }
  }
  return (
    <Modal onClose={closeModal} wide>
      <h3>{t("My settings", "我的设置")}</h3>

      <h4 className="sethead">{t("1 · My default quote coefficients (GP)", "1 · 我的默认报价系数（决定 GP）")}</h4>
      {user?.tools.includes("costing") ? <CoefEditor scope="mine" /> : <div className="dim">—</div>}

      <h4 className="sethead">{t("2 · Exchange rate", "2 · 汇率")}</h4>
      <div id="setFx" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
        {t("New cost sheets start with", "新建成本表的默认汇率")}: 1 USD =
        {admin ? <><input className="in n" id="setFxIn" style={{ maxWidth: 90 }} value={fx} onChange={(e) => setFx(e.target.value)} /> RMB
          <button className="btn sm" id="setFxSave" disabled={!(n(fx) > 0) || n(fx) === dfx} onClick={saveFx}>{t("Save (company-wide)", "保存（全公司）")}</button></>
          : <b className="mono">{dfx} RMB</b>}
        <span className="dim" style={{ fontSize: 12 }}>{admin ? t("one rate for everyone; each sheet keeps its own and can change it on the sheet", "全公司共用一个；每张单保存自己的汇率，单上可以改")
          : t("set by an admin; each sheet keeps its own rate and you can change it on the sheet", "由管理员设置；每张单保存自己的汇率，单上可以改")}</span>
      </div>

      <h4 className="sethead">{t("3 · Display", "3 · 显示")}</h4>
      <div id="setShow" style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
        <span>{t("Amounts in", "金额显示")} <span className="seg"><button className={cur === "USD" ? "on" : ""} data-setcur="USD" onClick={() => setCur("USD")}>$ USD</button><button className={cur === "CNY" ? "on" : ""} data-setcur="CNY" onClick={() => setCur("CNY")}>¥ RMB</button></span></span>
        <span>{t("Language", "语言")} <span className="seg"><button className={lang === "zh" ? "on" : ""} onClick={() => setLang("zh")}>中文</button><button className={lang === "en" ? "on" : ""} onClick={() => setLang("en")}>English</button></span></span>
        <span className="dim" style={{ fontSize: 12 }}>{t("remembered in this browser", "这台电脑的浏览器会记住")}</span>
      </div>

      <details style={{ marginTop: 16 }}>
        <summary className="dim" style={{ fontSize: 12.5, cursor: "pointer" }}>{t("AI on the server", "服务器上的 AI")}: {ai?.configured ? t("configured", "已配置") : t("not configured", "未配置")} · {ai?.model ?? "—"}</summary>
        <div className="kv" style={{ margin: "8px 0" }} id="aiStatus">
          <div>{t("AI", "AI")}</div><div>{ai?.configured ? t("configured", "已配置") : t("not configured (demo still works)", "未配置（演示模式照常可用）")}</div>
          <div>{t("Model", "模型")}</div><div className="mono">{ai?.model ?? "—"}</div>
          <div>{t("Web search", "联网搜索")}</div><div>{ai ? (ai.webSearch ? t("allowed", "允许") : t("off", "关闭")) : "—"}</div>
          <div>{t("History data", "历史数据")}</div><div>{meta ? `${meta.counts.indexed.toLocaleString()} ${t("styles with BOM", "个有 BOM 的款")} · ${meta.data.importedAt?.slice(0, 10) ?? ""}` : "—"}</div>
        </div>
        <div className="dim" style={{ fontSize: 12.5 }}>{t("The API key is kept on the server; the browser never sees it.", "API key 只放在服务器上，浏览器里没有。")}</div>
      </details>
      <div className="mb">
        <button className="btn p" onClick={closeModal}>{t("Close", "关闭")}</button>
      </div>
    </Modal>
  );
}
